import {PointerEvent as ReactPointerEvent, RefObject, useCallback, useRef, useState} from 'react';
import ReactQuill, {Quill} from 'react-quill-new';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {ImageResizeCorner} from '../components/ImageResizeOverlay';
import {applyIndicatorRect, ImageOverlayRect} from '../utils/applyIndicatorRect';

type QuillEditor = ReturnType<ReactQuill['getEditor']>;

const MIN_IMAGE_WIDTH = 24;

const IMAGE_MOVE_THRESHOLD = 4;

function measureImageOverlay(image: HTMLImageElement, wrapper: HTMLElement): ImageOverlayRect {
  const wrapperRect = wrapper.getBoundingClientRect();
  const imageRect = image.getBoundingClientRect();
  return {
    top: imageRect.top - wrapperRect.top,
    left: imageRect.left - wrapperRect.left,
    width: imageRect.width,
    height: imageRect.height,
  };
}

function applyOverlayRect(node: HTMLElement, rect: ImageOverlayRect) {
  node.style.top = `${rect.top}px`;
  node.style.left = `${rect.left}px`;
  node.style.width = `${rect.width}px`;
  node.style.height = `${rect.height}px`;
}

function getDropIndex(editor: QuillEditor, clientX: number, clientY: number): number | null {
  const caretRange = document.caretRangeFromPoint(clientX, clientY);
  if (isNil(caretRange)) {
    return null;
  }
  const blot = Quill.find(caretRange.startContainer, true);
  if (isNil(blot) || blot instanceof Quill) {
    return null;
  }
  return editor.getIndex(blot) + caretRange.startOffset;
}

function measureCaretIndicator(
  wrapper: HTMLElement,
  clientX: number,
  clientY: number
): ImageOverlayRect | null {
  const caretRange = document.caretRangeFromPoint(clientX, clientY);
  if (isNil(caretRange)) {
    return null;
  }
  const caretRect = caretRange.getBoundingClientRect();
  const wrapperRect = wrapper.getBoundingClientRect();
  return {
    top: caretRect.top - wrapperRect.top,
    left: caretRect.left - wrapperRect.left,
    width: caretRect.width,
    height: caretRect.height,
  };
}

export function useRichTextImageInteractions(
  quillRef: RefObject<ReactQuill | null>,
  wrapperRef: RefObject<HTMLDivElement | null>
) {
  const selectedImageRef = useRef<HTMLImageElement | null>(null);
  const dragImageRef = useRef<HTMLImageElement | null>(null);
  const moveDragRef = useRef<{startX: number; startY: number; isMoving: boolean} | null>(null);
  const moveIndicatorRef = useRef<HTMLDivElement | null>(null);
  const moveIndicatorRectRef = useRef<ImageOverlayRect | null>(null);
  const suppressClickRef = useRef(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const overlayRectRef = useRef<ImageOverlayRect | null>(null);
  const resizeDragCleanupRef = useRef<(() => void) | null>(null);
  const resizeDragRef = useRef<{
    startX: number;
    startWidth: number;
    directionX: number;
    currentWidth: number;
  } | null>(null);

  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [isMovingImage, setIsMovingImage] = useState(false);

  const positionOverlay = (rect: ImageOverlayRect) => {
    overlayRectRef.current = rect;
    if (isNotNil(overlayRef.current)) {
      applyOverlayRect(overlayRef.current, rect);
    }
  };

  const handleOverlayRef = useCallback((node: HTMLDivElement | null) => {
    overlayRef.current = node;
    if (isNotNil(node) && isNotNil(overlayRectRef.current)) {
      applyOverlayRect(node, overlayRectRef.current);
    }
  }, []);

  const handleMoveIndicatorRef = useCallback((node: HTMLDivElement | null) => {
    moveIndicatorRef.current = node;
    if (isNotNil(node) && isNotNil(moveIndicatorRectRef.current)) {
      applyIndicatorRect(node, moveIndicatorRectRef.current);
    }
  }, []);

  const attachImageInteractions = useCallback(
    (editor: QuillEditor) => {
      const handleEditorRootClick = (event: Event) => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        const target = event.target;
        const wrapper = wrapperRef.current;
        if (target instanceof HTMLImageElement && isNotNil(wrapper)) {
          selectedImageRef.current = target;
          setSelectedImage(target);
          const rect = measureImageOverlay(target, wrapper);
          overlayRectRef.current = rect;
          if (isNotNil(overlayRef.current)) {
            applyOverlayRect(overlayRef.current, rect);
          }
        } else {
          selectedImageRef.current = null;
          setSelectedImage(null);
          overlayRectRef.current = null;
        }
      };

      const repositionOverlay = () => {
        const image = selectedImageRef.current;
        const wrapper = wrapperRef.current;
        const node = overlayRef.current;
        if (isNotNil(image) && isNotNil(wrapper) && isNotNil(node)) {
          const rect = measureImageOverlay(image, wrapper);
          overlayRectRef.current = rect;
          applyOverlayRect(node, rect);
        }
      };

      const handleImageMovePointerMove = (event: PointerEvent) => {
        const dragState = moveDragRef.current;
        const image = dragImageRef.current;
        const wrapper = wrapperRef.current;
        if (isNil(dragState) || isNil(image) || isNil(wrapper)) {
          return;
        }
        if (!dragState.isMoving) {
          const distance = Math.hypot(
            event.clientX - dragState.startX,
            event.clientY - dragState.startY
          );
          if (distance < IMAGE_MOVE_THRESHOLD) {
            return;
          }
          dragState.isMoving = true;
          image.style.opacity = '0.4';
          editor.root.style.userSelect = 'none';
          wrapper.setAttribute('data-image-dragging', 'true');
          setIsMovingImage(true);
        }
        event.preventDefault();
        const indicatorRect = measureCaretIndicator(wrapper, event.clientX, event.clientY);
        if (isNotNil(indicatorRect)) {
          moveIndicatorRectRef.current = indicatorRect;
          if (isNotNil(moveIndicatorRef.current)) {
            applyIndicatorRect(moveIndicatorRef.current, indicatorRect);
          }
        }
      };

      const handleImageMovePointerUp = (event: PointerEvent) => {
        const dragState = moveDragRef.current;
        const image = dragImageRef.current;
        window.removeEventListener('pointermove', handleImageMovePointerMove);
        window.removeEventListener('pointerup', handleImageMovePointerUp);
        moveDragRef.current = null;
        dragImageRef.current = null;
        editor.root.style.userSelect = '';
        wrapperRef.current?.removeAttribute('data-image-dragging');
        setIsMovingImage(false);
        if (isNotNil(image)) {
          image.style.opacity = '';
        }
        if (isNil(dragState) || isNil(image) || !dragState.isMoving) {
          return;
        }
        suppressClickRef.current = true;
        const blot = Quill.find(image);
        const dropIndex = getDropIndex(editor, event.clientX, event.clientY);
        const source = image.getAttribute('src');
        if (isNil(blot) || blot instanceof Quill || isNil(dropIndex) || isNil(source)) {
          return;
        }
        const fromIndex = editor.getIndex(blot);
        const widthAttribute = image.getAttribute('width');
        editor.deleteText(fromIndex, 1, 'user');
        const insertIndex = dropIndex > fromIndex ? dropIndex - 1 : dropIndex;
        editor.insertEmbed(insertIndex, 'image', source, 'user');
        if (isNotNil(widthAttribute)) {
          editor.formatText(insertIndex, 1, {width: widthAttribute}, 'user');
        }
        editor.setSelection(insertIndex + 1, 0, 'user');
        selectedImageRef.current = null;
        setSelectedImage(null);
        overlayRectRef.current = null;
      };

      const handleImageMovePointerDown = (event: PointerEvent) => {
        if (event.button !== 0 || !(event.target instanceof HTMLImageElement)) {
          return;
        }
        dragImageRef.current = event.target;
        moveDragRef.current = {startX: event.clientX, startY: event.clientY, isMoving: false};
        window.addEventListener('pointermove', handleImageMovePointerMove);
        window.addEventListener('pointerup', handleImageMovePointerUp);
      };

      editor.root.addEventListener('click', handleEditorRootClick);
      editor.root.addEventListener('scroll', repositionOverlay);
      editor.root.addEventListener('pointerdown', handleImageMovePointerDown);
      window.addEventListener('resize', repositionOverlay);
      editor.root.addEventListener('load', repositionOverlay, true);
      editor.on('text-change', repositionOverlay);

      return () => {
        editor.root.removeEventListener('click', handleEditorRootClick);
        editor.root.removeEventListener('scroll', repositionOverlay);
        editor.root.removeEventListener('pointerdown', handleImageMovePointerDown);
        window.removeEventListener('resize', repositionOverlay);
        editor.root.removeEventListener('load', repositionOverlay, true);
        window.removeEventListener('pointermove', handleImageMovePointerMove);
        window.removeEventListener('pointerup', handleImageMovePointerUp);
        editor.off('text-change', repositionOverlay);
      };
    },
    [wrapperRef]
  );

  const handleImageResize = (width: number | null) => {
    const image = selectedImageRef.current;
    const editor = quillRef.current?.getEditor();
    if (isNil(image) || isNil(editor)) {
      return;
    }
    const blot = Quill.find(image);
    if (isNil(blot) || blot instanceof Quill) {
      selectedImageRef.current = null;
      setSelectedImage(null);
      overlayRectRef.current = null;
      return;
    }
    const index = editor.getIndex(blot);
    editor.formatText(
      index,
      1,
      {width: isNil(width) ? false : String(width), height: false},
      'user'
    );
  };

  const handleImageResizeDragMove = (event: PointerEvent) => {
    const dragState = resizeDragRef.current;
    const image = selectedImageRef.current;
    const wrapper = wrapperRef.current;
    if (isNil(dragState) || isNil(image) || isNil(wrapper)) {
      return;
    }
    const delta = (event.clientX - dragState.startX) * dragState.directionX;
    const nextWidth = Math.max(MIN_IMAGE_WIDTH, Math.round(dragState.startWidth + delta));
    dragState.currentWidth = nextWidth;
    image.style.width = `${nextWidth}px`;
    positionOverlay(measureImageOverlay(image, wrapper));
  };

  const handleImageResizeDragEnd = () => {
    const dragState = resizeDragRef.current;
    const image = selectedImageRef.current;
    if (isNotNil(dragState) && isNotNil(image)) {
      image.style.width = '';
      if (dragState.currentWidth !== dragState.startWidth) {
        handleImageResize(dragState.currentWidth);
      }
    }
    resizeDragRef.current = null;
    resizeDragCleanupRef.current?.();
    resizeDragCleanupRef.current = null;
  };

  const handleImageResizeDragStart = (
    event: ReactPointerEvent<HTMLDivElement>,
    corner: ImageResizeCorner
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const image = selectedImageRef.current;
    if (isNil(image)) {
      return;
    }
    const directionX = corner === 'topLeft' || corner === 'bottomLeft' ? -1 : 1;
    const startWidth = Math.round(image.getBoundingClientRect().width);
    resizeDragRef.current = {
      startX: event.clientX,
      startWidth,
      directionX,
      currentWidth: startWidth,
    };
    window.addEventListener('pointermove', handleImageResizeDragMove);
    window.addEventListener('pointerup', handleImageResizeDragEnd);
    resizeDragCleanupRef.current = () => {
      window.removeEventListener('pointermove', handleImageResizeDragMove);
      window.removeEventListener('pointerup', handleImageResizeDragEnd);
    };
  };

  const clearImageSelectionUnlessResizing = useCallback(() => {
    if (isNotNil(resizeDragRef.current)) {
      return;
    }
    selectedImageRef.current = null;
    setSelectedImage(null);
    overlayRectRef.current = null;
  }, []);

  return {
    selectedImage,
    isMovingImage,
    handleOverlayRef,
    handleMoveIndicatorRef,
    handleImageResizeDragStart,
    attachImageInteractions,
    clearImageSelectionUnlessResizing,
  };
}
